// ============================================================
// TERROR TCG — COMPANION SPRITE (component only)
//
// REGISTRY MERGE (July 3, 2026): this file used to RE-DECLARE the
// entire companion registry (COMPANIONS, TIERS, helpers, radiant
// tables) in parallel with companions.js — two sources of truth
// that had already drifted (divergent RADIANT_TIER_COST prices and
// a different RADIANT_VERSIONS catalog; the live UI + progression
// engine read companions.js, so those were the canon values).
//
// Now: companions.js is the ONE registry. This file keeps only the
// sprite component, and re-exports the registry names for
// back-compat so existing imports from "./CompanionSprite" (e.g.
// CompanionInteraction) keep working — they simply receive the
// canonical values.
//
// The 44 per-companion originLore lines that previously lived only
// here were ported INTO companions.js in the same deploy.
// ============================================================

import { useState, useEffect, useRef } from "react";
import { getCompanionSprite, getCompanionFallback } from "./SpriteManifest";
import { getCompanion, ILK_COLORS } from "./companions";

// ── Canonical registry re-exports (single source: companions.js) ──
export {
  TIERS,
  SOL_CONVERTER_COSTS,
  ILK_COLORS,
  VARIANTS,
  COMPANIONS,
  getCompanion,
  getSolConverterCost,
  getStarterCompanionId,
  isCompanionUnlocked,
  getUnlockedCompanions,
  getUnlockHint,
  getCompanionProvenance,
  groupCompanionsByTier,
  RADIANT_TIER_COST,
  RADIANT_VERSIONS,
} from "./companions";

// Radiant aura pulse keyframe — injected once, lifecycle-proof.
if (typeof document !== "undefined" && !document.getElementById("comp-radiant-kf")) {
  const el = document.createElement("style");
  el.id = "comp-radiant-kf";
  el.textContent =
    "@keyframes comp-radiant-pulse{0%,100%{opacity:0.7;transform:scale(0.98);}50%{opacity:1;transform:scale(1.04);}}";
  document.head.appendChild(el);
}

// ─────────────────────────────────────────────────────────────
// COMPANION SPRITE COMPONENT
// Renders the companion's real CDN art (the SAME source every other
// room uses via PlayerEntity), so the companion looks identical in the
// Plaza, the Affinity hall, dungeons, the workshop — everywhere.
//
//   companionId → `${CDN}/${id}.webp`  (own art)
//   on 404      → J'ynxi prototype art (graceful, never blank)
//   radiantTier → optional holo sheen ring behind the sprite
//
// This replaced the old procedural-orb placeholder, which is exactly
// why the Plaza companion looked different from the one in the Affinity
// room — Plaza drew a colored circle while the halls drew real art.
// One art source now, no drift.
// ─────────────────────────────────────────────────────────────

// Radiant sheen colors per holo tier (subtle aura ring behind sprite).
const RADIANT_AURA = {
  holo_silver:   "#d8d8e8",
  holo_gold:     "#f0d060",
  holo_void:     "#9933ff",
  holo_shadow:   "#7c3aed",
  holo_jynxi:    "#2ee66b",
  holo_depths:   "#00aaff",
  holo_solrend:  "#ff2266",
  holo_aeternus: "#ffffff",
  holo_mythic:   "#ff66cc",
};

function CompanionSpriteComponent({ companionId, size = 120, variant = "standard", radiantTier = null }) {
  const own = companionId ? getCompanionSprite(companionId) : null;
  const [fellBack, setFellBack] = useState(false);
  // Strike two (July 15, 2026): when the FALLBACK art also 404s — an
  // empty CDN, e.g. — the old code left a dead broken-image icon on
  // screen. Now the third state draws a generated spirit companion in
  // the companion's ilk color. Never a broken img again.
  const [artDead, setArtDead] = useState(false);
  const prevOwn = useRef(own);
  useEffect(() => {
    if (prevOwn.current !== own) { prevOwn.current = own; setFellBack(false); setArtDead(false); }
  }, [own]);

  if (!companionId) return null;

  const src = fellBack ? getCompanionFallback() : own;
  const aura = radiantTier ? (RADIANT_AURA[radiantTier] || null) : null;

  if (artDead) {
    const comp = getCompanion ? getCompanion(companionId) : null;
    const glow = aura || (comp && ILK_COLORS && ILK_COLORS[comp.ilk]) || "#c9a84c";
    return (
      <div style={{
        position: "relative", width: size, height: size,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        {aura && (
          <div style={{
            position: "absolute", inset: "6%", borderRadius: "50%",
            background: `radial-gradient(circle, ${aura}55 0%, ${aura}22 45%, transparent 72%)`,
            filter: "blur(2px)",
            animation: "comp-radiant-pulse 2.6s ease-in-out infinite",
            pointerEvents: "none",
          }} />
        )}
        {/* generated spirit — orb body, ears, eyes, in ilk color */}
        <div style={{ position: "relative", width: "72%", height: "72%",
          animation: "comp-radiant-pulse 3.2s ease-in-out infinite" }}>
          <div style={{ position: "absolute", top: "-8%", left: "18%", width: "20%", height: "34%",
            borderRadius: "50% 50% 0 0", background: `linear-gradient(180deg, ${glow}cc, ${glow}55)`,
            transform: "rotate(-14deg)" }} />
          <div style={{ position: "absolute", top: "-8%", right: "18%", width: "20%", height: "34%",
            borderRadius: "50% 50% 0 0", background: `linear-gradient(180deg, ${glow}cc, ${glow}55)`,
            transform: "rotate(14deg)" }} />
          <div style={{ position: "absolute", inset: "12% 8% 4% 8%", borderRadius: "50%",
            background: `radial-gradient(circle at 38% 32%, ${glow}ee 0%, ${glow}99 45%, ${glow}44 80%)`,
            boxShadow: `0 0 ${Math.max(6, size * 0.08)}px ${glow}88, inset -3px -4px 8px rgba(0,0,0,0.35)` }}>
            <div style={{ position: "absolute", top: "38%", left: "28%", width: "10%", height: "14%",
              minWidth: 2, minHeight: 3, borderRadius: "50%", background: "#0a0812" }} />
            <div style={{ position: "absolute", top: "38%", right: "28%", width: "10%", height: "14%",
              minWidth: 2, minHeight: 3, borderRadius: "50%", background: "#0a0812" }} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      position: "relative", width: size, height: size,
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      {aura && (
        <div style={{
          position: "absolute", inset: "6%", borderRadius: "50%",
          background: `radial-gradient(circle, ${aura}55 0%, ${aura}22 45%, transparent 72%)`,
          filter: "blur(2px)",
          animation: "comp-radiant-pulse 2.6s ease-in-out infinite",
          pointerEvents: "none",
        }} />
      )}
      <img
        key={src}
        src={src}
        alt="companion"
        onError={() => { if (!fellBack) setFellBack(true); else setArtDead(true); }}
        style={{
          width: "100%", height: "100%", objectFit: "contain",
          imageRendering: "pixelated",
          filter: aura ? `drop-shadow(0 0 8px ${aura}aa)` : "drop-shadow(0 0 6px #c9a84c44)",
          position: "relative", zIndex: 1,
        }}
      />
    </div>
  );
}

export default CompanionSpriteComponent;
